import { httpClient } from '@/core/api/httpClient';
import { toPascalCaseParams } from '@/shared/lib/to-pascal-case-params';
import { API_V1 } from '@/core/api/endpoints';
import { env } from '@/core/config/env';
import type {
  OrgSummaryDto,
  TaskStatusOverviewDto,
  OverdueTaskDto,
  ProjectHealthDto,
  TeamMemberWorkloadDto,
  TaskDistributionDto,
  TimeAnalysisDto,
  AllTaskItemDto,
  PagedResult,
  OverdueTasksParams,
  AllTasksParams,
} from '../types';
import {
  mockOrgSummary,
  mockTaskStatusOverview,
  mockOverdueTasks,
  mockProjectHealth,
  mockTeamWorkload,
  mockTaskDistribution,
  mockTimeAnalysis,
  mockAllTasks,
} from './analytics.mock';

export const analyticsService = {
  getOrgSummary: (signal?: AbortSignal): Promise<OrgSummaryDto> => {
    if (env.VITE_USE_MOCK === 'true') return Promise.resolve(mockOrgSummary);
    return (
      signal
        ? httpClient.get<OrgSummaryDto>(`${API_V1}/analytics/org-summary`, { signal })
        : httpClient.get<OrgSummaryDto>(`${API_V1}/analytics/org-summary`)
    ).then((r) => r.data);
  },

  getTaskStatusOverview: (signal?: AbortSignal): Promise<TaskStatusOverviewDto> => {
    if (env.VITE_USE_MOCK === 'true') return Promise.resolve(mockTaskStatusOverview);
    return (
      signal
        ? httpClient.get<TaskStatusOverviewDto>(`${API_V1}/analytics/task-status-overview`, {
            signal,
          })
        : httpClient.get<TaskStatusOverviewDto>(`${API_V1}/analytics/task-status-overview`)
    ).then((r) => r.data);
  },

  getOverdueTasks: (
    params?: OverdueTasksParams,
    signal?: AbortSignal,
  ): Promise<PagedResult<OverdueTaskDto>> => {
    if (env.VITE_USE_MOCK === 'true') return Promise.resolve(mockOverdueTasks);
    return httpClient
      .get<PagedResult<OverdueTaskDto>>(`${API_V1}/analytics/overdue-tasks`, {
        params: params ? toPascalCaseParams(params as Record<string, unknown>) : undefined,
        signal,
      })
      .then((r) => r.data);
  },

  getProjectHealth: (signal?: AbortSignal): Promise<ProjectHealthDto[]> => {
    if (env.VITE_USE_MOCK === 'true') return Promise.resolve(mockProjectHealth);
    return (
      signal
        ? httpClient.get<ProjectHealthDto[]>(`${API_V1}/analytics/project-health`, { signal })
        : httpClient.get<ProjectHealthDto[]>(`${API_V1}/analytics/project-health`)
    ).then((r) => r.data);
  },

  getTeamWorkload: (signal?: AbortSignal): Promise<TeamMemberWorkloadDto[]> => {
    if (env.VITE_USE_MOCK === 'true') return Promise.resolve(mockTeamWorkload);
    return (
      signal
        ? httpClient.get<TeamMemberWorkloadDto[]>(`${API_V1}/analytics/team-workload`, { signal })
        : httpClient.get<TeamMemberWorkloadDto[]>(`${API_V1}/analytics/team-workload`)
    ).then((r) => r.data);
  },

  getTaskDistribution: (signal?: AbortSignal): Promise<TaskDistributionDto> => {
    if (env.VITE_USE_MOCK === 'true') return Promise.resolve(mockTaskDistribution);
    return (
      signal
        ? httpClient.get<TaskDistributionDto>(`${API_V1}/analytics/task-distribution`, { signal })
        : httpClient.get<TaskDistributionDto>(`${API_V1}/analytics/task-distribution`)
    ).then((r) => r.data);
  },

  getTimeAnalysis: (signal?: AbortSignal): Promise<TimeAnalysisDto> => {
    if (env.VITE_USE_MOCK === 'true') return Promise.resolve(mockTimeAnalysis);
    return (
      signal
        ? httpClient.get<TimeAnalysisDto>(`${API_V1}/analytics/time-analysis`, { signal })
        : httpClient.get<TimeAnalysisDto>(`${API_V1}/analytics/time-analysis`)
    ).then((r) => r.data);
  },

  getAllTasks: (
    params?: AllTasksParams,
    signal?: AbortSignal,
  ): Promise<PagedResult<AllTaskItemDto>> => {
    if (env.VITE_USE_MOCK === 'true') return Promise.resolve(mockAllTasks);
    return httpClient
      .get<PagedResult<AllTaskItemDto>>(`${API_V1}/analytics/all-tasks`, {
        params: params ? toPascalCaseParams(params as Record<string, unknown>) : undefined,
        signal,
      })
      .then((r) => r.data);
  },
};
